/**
 * Column on the Kanban board. Hosts a dnd-kit sortable context for
 * its task cards, renders the column header with optional WIP state,
 * and exposes a "+ Add task" affordance per spec section 15.4.
 *
 * WIP states (Doing column only) per design README section 5.2:
 *   - below limit: standard header
 *   - at limit: faint orange wash + orange WIP chip on tint
 *   - over limit: stronger wash + solid orange WIP chip + bottom border
 */

import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { Task, TaskStatus } from '@workspace/shared';

import { cn } from '@/lib/utils';
import { TaskCard } from './TaskCard';

interface TaskColumnProps {
  status: TaskStatus;
  label: string;
  tasks: Task[];
  /** Doing column only. Other statuses pass null. */
  wipLimit: number | null;
  /** Optional id of the most recently auto-pulled task so the card
   * surfaces the brief flash + chip. */
  autoPulledTaskId: string | null;
  onCardClick: (id: string) => void;
  onAddClick: (status: TaskStatus) => void;
}

export function TaskColumn({
  status,
  label,
  tasks,
  wipLimit,
  autoPulledTaskId,
  onCardClick,
  onAddClick,
}: TaskColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `column-${status}`, data: { status } });

  // WIP state computation. Doing column is the only one with a limit.
  const count = tasks.length;
  const wipBucket =
    wipLimit !== null ? (count > wipLimit ? 'over' : count === wipLimit ? 'at' : 'under') : 'under';

  return (
    <div
      ref={setNodeRef}
      data-testid={`column-${status}`}
      data-wip-state={wipBucket}
      className={cn(
        'flex h-full min-w-[260px] flex-1 flex-col border-r border-mx-grey-800 bg-mx-near-black px-mx-3 py-mx-4',
        wipBucket === 'at' && 'bg-mx-orange-tint',
        wipBucket === 'over' && 'border-b-2 border-b-mx-orange bg-mx-orange-tint-strong',
        isOver && 'ring-1 ring-inset ring-mx-orange/40',
      )}
    >
      <header className="mb-mx-3 flex items-center justify-between">
        <p className="mono-label text-mx-grey-300">
          {label} <span className="text-mx-grey-mid">({count})</span>
        </p>
        {wipLimit !== null && (
          <span
            className={cn(
              'mono-label px-mx-2 py-[2px] text-mx-mono-sm',
              wipBucket === 'under' && 'text-mx-grey-mid',
              wipBucket === 'at' && 'bg-mx-orange-tint-strong text-mx-orange',
              wipBucket === 'over' && 'bg-mx-orange text-mx-white',
            )}
          >
            WIP {count}/{wipLimit}
          </span>
        )}
      </header>

      <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        <ul className="flex flex-1 flex-col gap-mx-2 overflow-y-auto">
          {tasks.map((task) => (
            <li key={task.id}>
              <TaskCard
                task={task}
                autoPulled={task.id === autoPulledTaskId}
                onClick={onCardClick}
              />
            </li>
          ))}
        </ul>
      </SortableContext>

      <button
        type="button"
        onClick={() => onAddClick(status)}
        className="mt-mx-3 flex h-8 items-center justify-center gap-mx-2 border border-dashed border-mx-grey-800 text-mx-mono text-mx-grey-mid transition-colors duration-mx-default ease-mx-default hover:border-mx-grey-700 hover:text-mx-white"
      >
        <PlusIcon className="h-3 w-3" strokeWidth={1.6} aria-hidden />
        ADD TASK
      </button>
    </div>
  );
}
